import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiRequest, getStoreIdFromToken } from '../../utils/api';
import backIcon from '../../assets/icons/common/back_icon.png';

/**
 * 매장 매출 대시보드 — WowOrder(QR) 채널 매출. 매장설정 '매출 보기'로 진입.
 * 백엔드 /api/sales/:storeId/{meta,period,orders,menu} 집계 소비.
 * 연/월/일 드릴다운(기간별) + 메뉴별 순위/미판매. okdc 는 항목 매출 없어 수량 기준.
 */
const DOW = ['일', '월', '화', '수', '목', '금', '토'];
const won = (n) => (Number(n) || 0).toLocaleString('ko-KR');
const daysInMonth = (y, m) => new Date(y, m, 0).getDate(); // m=1..12

const SalesPage = ({ isOpen, onClose, storeId: storeIdProp, posType }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [storeId, setStoreId] = useState(storeIdProp || null);

  const [tab, setTab] = useState('period');   // 'period' | 'menu'
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(null);    // null=연뷰
  const [day, setDay] = useState(null);        // 'YYYY-MM-DD' | null
  const [sort, setSort] = useState('revenue'); // 'revenue'|'qty'|'name'

  const [meta, setMeta] = useState(null);
  const [period, setPeriod] = useState(null);
  const [orders, setOrders] = useState(null);
  const [menu, setMenu] = useState(null);
  const [loading, setLoading] = useState(false);

  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);
  const dayRef = useRef(day);
  useEffect(() => { dayRef.current = day; }, [day]);

  const revenueReliable = !(posType === 'okdc');

  // 열림/닫힘 애니메이션 + storeId 확보 + meta 로드
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      setTimeout(() => setIsAnimating(true), 10);
      const sid = storeIdProp || getStoreIdFromToken();
      setStoreId(sid);
      setTab('period'); setMonth(null); setDay(null); setSort(revenueReliable ? 'revenue' : 'qty');
      (async () => {
        try {
          const r = await apiRequest(`/sales/${sid}/meta`);
          if (r && r.success) { setMeta(r); setYear((r.years && r.years[0]) || new Date().getFullYear()); }
        } catch (e) { /* ignore */ }
      })();
    } else {
      setIsAnimating(false);
      const t = setTimeout(() => setIsVisible(false), 150);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // 히스토리 entry + 뒤로가기(일 드릴 먼저, 그다음 페이지 닫힘)
  useEffect(() => {
    if (!isOpen) return;
    window.history.pushState({ salesPage: true }, '', window.location.href);
    const handleBack = () => {
      if (dayRef.current != null) { setDay(null); return; }
      if (window.history.state?.salesPage) return;
      onCloseRef.current?.();
    };
    window.handleAndroidBack = handleBack;
    const onPop = () => handleBack();
    window.addEventListener('popstate', onPop);
    return () => {
      window.handleAndroidBack = null;
      window.removeEventListener('popstate', onPop);
      if (window.history.state?.salesPage) window.history.back();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // 화면 스케일
  useEffect(() => {
    const scaleUI = () => {
      const w = document.querySelector('.sales-page-wrapper');
      if (w) w.style.zoom = Math.min(window.innerWidth / 360, 1.5);
    };
    if (isVisible) { scaleUI(); window.addEventListener('resize', scaleUI); }
    return () => window.removeEventListener('resize', scaleUI);
  }, [isVisible]);

  // 기간별 데이터
  useEffect(() => {
    if (!isOpen || !storeId || tab !== 'period') return;
    if (day != null) {
      setLoading(true);
      apiRequest(`/sales/${storeId}/orders?date=${day}`)
        .then((r) => { if (r && r.success) setOrders(r); })
        .catch(() => {}).finally(() => setLoading(false));
    } else {
      setLoading(true);
      const q = `year=${year}${month ? `&month=${month}` : ''}`;
      apiRequest(`/sales/${storeId}/period?${q}`)
        .then((r) => { if (r && r.success) setPeriod(r); })
        .catch(() => {}).finally(() => setLoading(false));
    }
  }, [isOpen, storeId, tab, year, month, day]);

  // 메뉴별 데이터 — 스코프(연/월/일) 반영. 일 선택 시 그날 메뉴 순위.
  useEffect(() => {
    if (!isOpen || !storeId || tab !== 'menu') return;
    setLoading(true);
    const q = day != null
      ? `date=${day}&sort=${sort}`
      : `year=${year}${month ? `&month=${month}` : ''}&sort=${sort}`;
    apiRequest(`/sales/${storeId}/menu?${q}`)
      .then((r) => { if (r && r.success) setMenu(r); })
      .catch(() => {}).finally(() => setLoading(false));
  }, [isOpen, storeId, tab, year, month, day, sort]);

  const handleClose = useCallback(() => onCloseRef.current?.(), []);

  if (!isVisible) return null;

  const scopeName = () => {
    if (day != null) { const [, mm, dd] = day.split('-').map(Number); const d = new Date(year, mm - 1, dd); return `${year}년 ${mm}월 ${dd}일 (${DOW[d.getDay()]})`; }
    if (month != null) return `${year}년 ${month}월`;
    return `${year}년`;
  };

  // 차트 유닛 구성 (연뷰=월, 월뷰=일)
  const buildUnits = () => {
    const map = {};
    (period?.buckets || []).forEach((b) => { map[b.bucket] = b; });
    const units = [];
    if (month == null) {
      for (let m = 1; m <= 12; m++) units.push({ label: m, revenue: map[m]?.revenue || 0, wknd: false, go: () => setMonth(m) });
    } else {
      const last = daysInMonth(year, month);
      for (let d = 1; d <= last; d++) {
        const dt = new Date(year, month - 1, d);
        units.push({ label: d, revenue: map[d]?.revenue || 0, wknd: dt.getDay() === 0 || dt.getDay() === 6, go: () => setDay(`${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`) });
      }
    }
    return units;
  };

  // 일 선택 시엔 그날 주문내역에서 hero 계산(데모처럼 스코프=일 반영). 아니면 월/연 period.hero.
  const dayHero = (day != null && orders) ? (() => {
    const list = orders.orders || [];
    const revenue = list.reduce((s, o) => s + (o.amount || 0), 0);
    const cnt = list.length;
    return { revenue, orders: cnt, aov: cnt ? Math.round(revenue / cnt) : 0, deltaPct: null };
  })() : null;
  const hero = day != null ? dayHero : period?.hero;
  const units = tab === 'period' && day == null ? buildUnits() : [];
  const maxRev = Math.max(1, ...units.map((u) => u.revenue));
  const sortedMenu = menu?.ranking || [];
  const menuTop = Math.max(1, ...sortedMenu.map((m) => (revenueReliable && sort !== 'qty' ? m.revenue : m.qty)));

  return (
    <div className={`sales-page ${isAnimating ? 'open' : ''}`}>
      <div className="sales-page-wrapper">
        <div className="sp-header">
          <button type="button" className="sp-back" onClick={handleClose} aria-label="뒤로"><img src={backIcon} alt="뒤로" /></button>
          <span className="sp-title">매출</span>
          <span style={{ width: 40, flexShrink: 0 }} />
        </div>

        <div className="sp-body">
          {/* 컨트롤: 연 + 탭 */}
          <div className="sp-controls">
            <div className="sp-yearsel">
              <select value={year} onChange={(e) => { setYear(Number(e.target.value)); setMonth(null); setDay(null); }}>
                {(meta?.years || [year]).map((y) => <option key={y} value={y}>{y}년</option>)}
              </select>
            </div>
            <div className="sp-tabs">
              <button className={tab === 'period' ? 'on' : ''} onClick={() => setTab('period')}>기간별</button>
              <button className={tab === 'menu' ? 'on' : ''} onClick={() => setTab('menu')}>메뉴별</button>
            </div>
          </div>

          {/* breadcrumb */}
          <div className="sp-crumbs">
            <button className={month == null ? 'cur' : ''} onClick={() => { setMonth(null); setDay(null); }} disabled={month == null}>{year}년</button>
            {month != null && <><span className="sep">›</span><button className={day == null ? 'cur' : ''} onClick={() => setDay(null)} disabled={day == null}>{month}월</button></>}
            {day != null && <><span className="sep">›</span><span className="cur">{Number(day.split('-')[2])}일</span></>}
          </div>

          {/* hero */}
          <div className="sp-hero">
            <div className="sp-eyebrow"><span className="k">매출</span><span className="scope">{scopeName()}</span></div>
            <div className="sp-amount"><span className="num">{won(hero?.revenue)}</span><span className="won">원</span></div>
            {hero?.deltaPct != null && (
              <div className={`sp-delta ${hero.deltaPct >= 0 ? 'up' : 'down'}`}>
                <span>{hero.deltaPct >= 0 ? '+' : ''}{hero.deltaPct}%</span>
                <span className="cmp">{month == null ? '전년' : (day == null ? '전월' : '전일')} 대비</span>
              </div>
            )}
            <div className="sp-split">
              <div><span className="k">주문</span><span className="v">{won(hero?.orders)}<small>건</small></span></div>
              <div><span className="k">객단가</span><span className="v">{won(hero?.aov)}<small>원</small></span></div>
            </div>
          </div>

          {tab === 'period' ? (
            day != null ? (
              /* 일 주문내역 */
              <div className="sp-sec">
                <div className="sp-sechead"><span className="t">주문 내역</span><span className="n">{(orders?.orders || []).length}건</span></div>
                <div className="sp-orders">
                  {(orders?.orders || []).length === 0 ? <div className="sp-empty">주문이 없습니다</div> :
                    orders.orders.map((o, i) => (
                      <div className="sp-ord" key={i}>
                        <span className="t">{o.time}</span>
                        <span className="items">{o.items.map((it, j) => <span key={j} className="li">{it.name}{it.qty > 1 ? ` ×${it.qty}` : ''}</span>)}</span>
                        <span className="amt">{won(o.amount)}원</span>
                      </div>
                    ))}
                </div>
              </div>
            ) : (
              /* 막대차트 */
              <div className="sp-sec">
                <div className="sp-sechead"><span className="t">{month == null ? '월별 매출' : '일별 매출'}</span><span className="n">막대를 누르면 상세</span></div>
                <div className="sp-chartcard">
                  <div className="sp-rail">
                    {units.map((u, i) => (
                      <button key={i} className={`sp-unit ${u.wknd ? 'wknd' : ''}`} disabled={!u.revenue} onClick={u.revenue ? u.go : undefined}>
                        <span className="sp-barslot"><span className={`sp-bar ${u.revenue ? '' : 'none'}`} style={{ height: u.revenue ? `${Math.max(6, u.revenue / maxRev * 100)}%` : '2px' }} /></span>
                        <span className="sp-tick">{u.label}</span>
                      </button>
                    ))}
                  </div>
                  <div className="sp-chartfoot"><span>최고 <b>{won(maxRev > 1 ? maxRev : 0)}원</b></span></div>
                </div>
              </div>
            )
          ) : (
            /* 메뉴별 */
            <div className="sp-sec">
              <div className="sp-sorts">
                {revenueReliable && <button className={sort === 'revenue' ? 'on' : ''} onClick={() => setSort('revenue')}>매출순</button>}
                <button className={sort === 'qty' ? 'on' : ''} onClick={() => setSort('qty')}>판매량순</button>
                <button className={sort === 'name' ? 'on' : ''} onClick={() => setSort('name')}>이름순</button>
              </div>
              <div className="sp-menu">
                {sortedMenu.length === 0 ? <div className="sp-empty">이 기간엔 주문이 없습니다</div> :
                  sortedMenu.map((m, i) => {
                    const v = revenueReliable && sort !== 'qty' ? m.revenue : m.qty;
                    return (
                      <div className={`sp-row ${sort !== 'name' && i < 3 ? 'top3' : ''}`} key={m.name}>
                        <span className="rank">{i + 1}</span>
                        <span className="mid"><span className="name">{m.name}</span><span className="track"><span className="fill" style={{ width: `${v / menuTop * 100}%` }} /></span></span>
                        <span className="figs">{revenueReliable && <div className="money">{won(m.revenue)}원</div>}<div className="qty">{won(m.qty)}개</div></span>
                      </div>
                    );
                  })}
              </div>
              {(menu?.zeroMenus || []).length > 0 && (
                <div className="sp-zero">
                  <div className="sp-zerohead"><b>한 번도 안 나간 메뉴</b><span>{menu.zeroMenus.length}개</span></div>
                  <div className="sp-chips">{menu.zeroMenus.map((n, i) => <span className="chip" key={i}>{n}</span>)}</div>
                </div>
              )}
            </div>
          )}

          <p className="sp-disc">와우오더로 접수된 주문만 집계됩니다. 홀 직접 주문·현금 결제·POS 직접 입력분은 포함되지 않아 POS 마감 금액과 차이가 날 수 있습니다.</p>
        </div>
      </div>

      <style jsx>{`
        .sales-page { position: fixed; inset: 0; z-index: 10000; background: #ffffff;
          transform: translateX(100%); transition: transform 0.15s cubic-bezier(0.4,0,0.2,1);
          font-family: 'Pretendard Variable', Pretendard, -apple-system, system-ui, sans-serif; }
        .sales-page.open { transform: translateX(0); }
        .sales-page-wrapper { width: 360px; height: 100%; margin: 0 auto; display: flex; flex-direction: column; background: #F7F8FA; }
        .sp-header { display: flex; align-items: center; height: 56px; padding: 0 8px; background: #fff; border-bottom: 1px solid #f0f0f0; flex-shrink: 0; }
        .sp-back { width: 40px; height: 40px; border: none; background: transparent; cursor: pointer; display: flex; align-items: center; justify-content: center; padding: 0; }
        .sp-back img { width: 22px; height: 22px; }
        .sp-title { flex: 1; text-align: center; font-size: 17px; font-weight: 700; color: #2b2b2b; }
        .sp-body { flex: 1; overflow-y: auto; padding: 14px 16px 40px; }

        .sp-controls { display: flex; gap: 8px; margin-bottom: 10px; }
        .sp-yearsel { position: relative; }
        .sp-yearsel select { appearance: none; font-size: 13px; font-weight: 700; color: #212529; background: #fff;
          border: 1px solid #E8E8E8; border-radius: 11px; padding: 10px 30px 10px 13px; cursor: pointer; }
        .sp-yearsel::after { content: ""; position: absolute; right: 12px; top: 50%; width: 6px; height: 6px;
          border-right: 1.6px solid #999; border-bottom: 1.6px solid #999; transform: translateY(-70%) rotate(45deg); pointer-events: none; }
        .sp-tabs { display: grid; grid-template-columns: 1fr 1fr; gap: 4px; flex: 1; background: #fff; border: 1px solid #E8E8E8; border-radius: 11px; padding: 4px; }
        .sp-tabs button { font-size: 12.5px; font-weight: 600; border: 0; background: transparent; color: #888; padding: 8px 0; border-radius: 8px; cursor: pointer; }
        .sp-tabs button.on { background: #212529; color: #fff; }

        .sp-crumbs { display: flex; align-items: center; gap: 4px; flex-wrap: wrap; padding: 2px 2px 12px; font-size: 12px; }
        .sp-crumbs button { border: 0; background: none; cursor: pointer; color: #C08A00; padding: 2px 0; font-weight: 600; }
        .sp-crumbs button:disabled, .sp-crumbs .cur { color: #212529; font-weight: 700; cursor: default; }
        .sp-crumbs .sep { color: #b8c1c8; }

        .sp-hero { background: #fff; border: 1px solid #E8E8E8; border-radius: 16px; padding: 18px 16px 14px; }
        .sp-eyebrow { display: flex; align-items: baseline; gap: 8px; margin-bottom: 8px; }
        .sp-eyebrow .k { font-size: 10px; font-weight: 700; letter-spacing: .16em; color: #999; }
        .sp-eyebrow .scope { font-size: 13px; font-weight: 600; color: #212529; }
        .sp-amount { display: flex; align-items: baseline; gap: 4px; font-weight: 800; letter-spacing: -.04em; }
        .sp-amount .num { font-size: 40px; color: #212529; font-variant-numeric: tabular-nums; }
        .sp-amount .won { font-size: 19px; font-weight: 700; color: #39434c; }
        .sp-delta { display: inline-flex; align-items: center; gap: 6px; margin-top: 10px; font-size: 11px; font-weight: 600; padding: 5px 9px; border-radius: 7px; }
        .sp-delta.up { background: rgba(15,138,95,.09); color: #0f8a5f; }
        .sp-delta.down { background: rgba(217,72,59,.09); color: #d9483b; }
        .sp-delta .cmp { color: #999; font-weight: 400; }
        .sp-split { display: grid; grid-template-columns: 1fr 1fr; margin-top: 16px; border-top: 1px solid #eef1f4; }
        .sp-split > div { padding: 12px 0 2px; }
        .sp-split > div:first-child { border-right: 1px solid #eef1f4; }
        .sp-split > div:last-child { padding-left: 16px; }
        .sp-split .k { font-size: 10px; letter-spacing: .1em; color: #999; display: block; margin-bottom: 4px; }
        .sp-split .v { font-size: 18px; font-weight: 700; color: #212529; font-variant-numeric: tabular-nums; }
        .sp-split .v small { font-size: 12px; font-weight: 600; color: #39434c; margin-left: 1px; }

        .sp-sec { margin-top: 22px; }
        .sp-sechead { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; padding: 0 2px; }
        .sp-sechead .t { font-size: 13.5px; font-weight: 700; color: #212529; }
        .sp-sechead .n { font-size: 10px; color: #999; }

        .sp-chartcard { background: #fff; border: 1px solid #E8E8E8; border-radius: 16px; padding: 16px 4px 8px; }
        .sp-rail { display: flex; align-items: flex-end; height: 130px; padding: 0 10px; overflow-x: auto; }
        .sp-rail::-webkit-scrollbar { display: none; }
        .sp-unit { flex: 1 0 auto; min-width: 19px; display: flex; flex-direction: column; align-items: center; justify-content: flex-end; height: 100%; background: none; border: 0; padding: 0 2px; cursor: pointer; }
        .sp-unit:disabled { cursor: default; }
        .sp-barslot { flex: 1; width: 100%; display: flex; align-items: flex-end; justify-content: center; padding-bottom: 7px; }
        .sp-bar { width: 9px; border-radius: 3px 3px 1px 1px; background: #FDCD27; transition: height .4s cubic-bezier(.2,.8,.3,1); }
        .sp-unit.wknd .sp-bar:not(.none) { background: #FCE38C; }
        .sp-bar.none { background: transparent; border-bottom: 2px dotted #cdd5db; border-radius: 0; }
        .sp-tick { font-size: 9.5px; color: #a5b0b9; }
        .sp-chartfoot { display: flex; justify-content: flex-end; padding: 10px 14px 2px; margin-top: 6px; border-top: 1px solid #eef1f4; font-size: 10px; color: #999; }
        .sp-chartfoot b { color: #39434c; font-weight: 600; }

        .sp-orders { background: #fff; border: 1px solid #E8E8E8; border-radius: 16px; padding: 4px 16px 8px; }
        .sp-ord { display: grid; grid-template-columns: auto 1fr auto; gap: 11px; align-items: start; padding: 13px 0; border-bottom: 1px solid #eef1f4; }
        .sp-ord:last-child { border-bottom: 0; }
        .sp-ord .t { font-size: 11px; color: #999; padding-top: 2px; }
        .sp-ord .items { font-size: 12.5px; line-height: 1.55; display: flex; flex-direction: column; }
        .sp-ord .amt { font-size: 13px; font-weight: 700; color: #212529; font-variant-numeric: tabular-nums; white-space: nowrap; }

        .sp-sorts { display: flex; gap: 5px; margin-bottom: 11px; }
        .sp-sorts button { font-size: 11.5px; font-weight: 600; border: 1px solid #E8E8E8; background: #fff; color: #888; padding: 8px 12px; border-radius: 9px; cursor: pointer; }
        .sp-sorts button.on { background: #212529; color: #fff; border-color: #212529; }
        .sp-menu { background: #fff; border: 1px solid #E8E8E8; border-radius: 16px; padding: 6px 16px 10px; }
        .sp-row { display: grid; grid-template-columns: 18px 1fr auto; align-items: center; gap: 11px; padding: 13px 0; border-bottom: 1px solid #eef1f4; }
        .sp-row:last-child { border-bottom: 0; }
        .sp-row .rank { font-size: 11px; font-weight: 600; color: #b3bcc4; text-align: right; }
        .sp-row.top3 .rank { color: #C08A00; }
        .sp-row .name { font-size: 13.5px; font-weight: 600; color: #212529; margin-bottom: 6px; display: block; }
        .sp-row .track { height: 3px; background: #eef1f4; border-radius: 2px; overflow: hidden; display: block; }
        .sp-row .fill { height: 100%; background: #FDCD27; border-radius: 2px; display: block; transition: width .5s cubic-bezier(.2,.8,.3,1); }
        .sp-row .figs { text-align: right; font-variant-numeric: tabular-nums; }
        .sp-row .money { font-size: 13px; font-weight: 700; color: #212529; }
        .sp-row .qty { font-size: 10px; color: #999; margin-top: 3px; }

        .sp-zero { background: #fff; border: 1px solid #E8E8E8; border-radius: 16px; padding: 15px 16px; margin-top: 11px; }
        .sp-zerohead { display: flex; align-items: center; gap: 7px; margin-bottom: 11px; }
        .sp-zerohead b { font-size: 12.5px; font-weight: 700; color: #212529; }
        .sp-zerohead span { font-size: 10px; color: #999; }
        .sp-chips { display: flex; flex-wrap: wrap; gap: 6px; }
        .sp-chip, .sp-chips .chip { font-size: 11.5px; color: #39434c; background: #f2f5f7; border: 1px solid #eef1f4; border-radius: 7px; padding: 6px 9px; }

        .sp-empty { text-align: center; padding: 30px 0; color: #999; font-size: 12.5px; }
        .sp-disc { margin-top: 16px; padding: 0 4px; font-size: 11px; line-height: 1.6; color: #999; }
      `}</style>
    </div>
  );
};

export default SalesPage;
